import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Upload, message, Space } from 'antd';
import { ArrowRightOutlined, UploadOutlined, CrownOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { superadminUsersApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { FormSkeleton } from '../../components/SkeletonLoaders';

const { Option } = Select;

const normFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList?.slice(-1);
};

const SuperadminUserCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const res = await superadminUsersApi.getCreateMeta();
        const d = res?.data || res;
        if (!cancelled) setRoles(d.roles || []);
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت اطلاعات');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values) => {
    const fd = new FormData();
    fd.append('name', values.name?.trim() || '');
    fd.append('email', values.email?.trim() || '');
    fd.append('role', String(values.role ?? ''));
    const logoList = values.image;
    const file = logoList?.[0]?.originFileObj || logoList?.[0];
    if (file && file instanceof Blob) {
      fd.append('image', file);
    }
    setSubmitting(true);
    try {
      await superadminUsersApi.create(fd);
      message.success('کاربر ثبت شد');
      navigate('/settings/superadmin-users');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ثبت');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                <CrownOutlined className="text-xl text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">کاربر سوپر ادمین جدید</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">نام، ایمیل، نقش و تصویر پروفایل</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/settings/superadmin-users')}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loadingMeta ? (
                <FormSkeleton fields={5} />
              ) : (
                <Form form={form} layout="vertical" onFinish={onFinish}>
                  <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                    <Form.Item name="name" label="نام" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="email" label="ایمیل" rules={[{ required: true, type: 'email', message: 'ایمیل معتبر وارد کنید' }]}>
                      <Input dir="ltr" />
                    </Form.Item>
                    <Form.Item name="role" label="نقش" rules={[{ required: true, message: 'نقش را انتخاب کنید' }]}>
                      <Select popupClassName="custom-font" placeholder="نقش">
                        {roles.map((r) => (
                          <Option key={r.id} value={r.id}>
                            {r.display_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <Form.Item name="image" label="تصویر" valuePropName="fileList" getValueFromEvent={normFile}>
                    <Upload maxCount={1} beforeUpload={() => false} accept="image/*">
                      <Button icon={<UploadOutlined />}>انتخاب تصویر</Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                        ثبت
                      </Button>
                      <Button onClick={() => navigate('/settings/superadmin-users')}>انصراف</Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminUserCreate;
